import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useAppData } from '@/app-data';
import * as api from '@/features/backlog/api';
import { boardPath } from '@/constants/routes';
import { isDesktopApp } from '@/features/identity/desktop-updates';
import { Button } from '@/components/ui/Button';
import { SearchField } from '@/components/ui/SearchField';
import { inputCls } from '@/components/ui/form';
import { Plus } from '@/components/ui/icons';
import { Spinner } from '@/components/ui/Spinner';
import AreaNav from '@/components/area-nav';
import IdentityMenu from '@/features/identity/identity-menu';

/**
 * The home / landing page at "/" — a UUID-free index of all boards. Pick one (→ its friendly
 * /:slug URL), search by name, or create a new one (the server assigns its stable slug + key
 * prefix). No board chrome here (there is no current board yet), just a slim top bar.
 */
export default function BoardsIndexPage() {
  const { boards, loading, reloadBoards } = useAppData();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const addingRef = useRef(false);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const sorted = [...boards].sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));
    return needle ? sorted.filter((b) => b.name.toLowerCase().includes(needle) || b.slug.includes(needle)) : sorted;
  }, [boards, q]);

  const create = async () => {
    const trimmed = name.trim();
    if (!trimmed || addingRef.current) return;
    addingRef.current = true;
    setBusy(true);
    try {
      const board = await api.createBoard({ name: trimmed });
      await reloadBoards();
      navigate(boardPath(board.slug));
    } finally {
      addingRef.current = false;
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink text-fg">
      <header className="sticky top-0 z-30 border-b border-line bg-ink/80 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-[1100px] items-center justify-between gap-4 px-6">
          <div className="flex items-center gap-3">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand/15 text-brand">
              <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                <rect x="1.5" y="1.5" width="5.5" height="5.5" rx="1.3" fill="currentColor" />
                <rect x="9" y="1.5" width="5.5" height="5.5" rx="1.3" fill="currentColor" opacity="0.7" />
                <rect x="1.5" y="9" width="5.5" height="5.5" rx="1.3" fill="currentColor" opacity="0.7" />
                <rect x="9" y="9" width="5.5" height="5.5" rx="1.3" fill="currentColor" opacity="0.45" />
              </svg>
            </span>
            <span className="text-[14px] font-semibold text-fg-strong">spark-board</span>
          </div>
          <AreaNav />
          <div className="flex items-center gap-1">
            {/* Desktop app: the account lives in the tab strip (see DesktopHeaderProxy). */}
            {isDesktopApp() ? null : <IdentityMenu />}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1100px] px-6 py-10">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-[22px] font-bold text-fg-strong">Boards</h1>
            <p className="mt-1 text-[13px] text-fg-muted">Pick a board to open it, or create a new one.</p>
          </div>
          <div className="flex items-center gap-2">
            <SearchField value={q} onChange={setQ} placeholder="Search boards…" />
            {!creating ? (
              <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
                <Plus /> New board
              </Button>
            ) : null}
          </div>
        </div>

        {creating ? (
          <div className="mb-6 flex items-center gap-2 rounded-xl border border-line bg-surface-inset p-2">
            <input
              autoFocus
              className={inputCls}
              placeholder="Board name (e.g. Marketing Backlog)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') create();
                if (e.key === 'Escape') {
                  setCreating(false);
                  setName('');
                }
              }}
            />
            <Button variant="primary" size="sm" disabled={busy || !name.trim()} onClick={create}>
              Create
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setCreating(false);
                setName('');
              }}
            >
              Cancel
            </Button>
          </div>
        ) : null}

        {loading ? (
          <div className="flex justify-center py-24">
            <Spinner className="h-6 w-6" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl border border-line bg-surface-raised px-6 py-16 text-center text-[13px] text-fg-faint">
            {q.trim() ? `No boards match “${q.trim()}”.` : 'No boards yet — create your first one above.'}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((b) => (
              <Link
                key={b.id}
                to={boardPath(b.slug)}
                className="group flex flex-col rounded-2xl border border-line bg-surface-raised p-4 transition-colors hover:border-line-strong hover:bg-layer"
              >
                <div className="mb-3 flex items-center gap-2">
                  <span className="item-key rounded-md px-1.5 py-0.5 text-[11px] font-semibold">{b.key_prefix}</span>
                  <span className="tnum text-[11px] text-fg-faint">/{b.slug}</span>
                </div>
                <div className="text-[15px] font-semibold text-fg-strong group-hover:text-fg">{b.name}</div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
